// ExportSchedulesDoctor.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface ScheduleItem {
    timeStart: string      // "HH:mm:ss"
    durationMinute: number
    cnNumber: string | null
    branchCnNumber: string | null
    snCode?: string | null
    patientFullName: string
    phone: string | null
    operativeTypeName: string | null
    operativeName: string
    statusAppointment: string
    price: number
    lab: string
    xray: string
    specialInfo: string | null
    practice: string
    remark: string
    createdBy: string
    updatedBy: string
    doctorFullName: string
    doctorId: number
}

export interface DayGroup {
    date: string            // "YYYY-MM-DD"
    obj: ScheduleItem[]
}

export interface SchedulesDoctorExcelData {
    branchName: string
    dateStart: string
    dateEnd: string
    doctorsFilter?: string
    statusFilter?: string
    operativeTypeFilter?: string
    operativeFilter?: string
    groups: DayGroup[],
    snType: boolean
    t: (key: string) => string
}

export async function ExportSchedulesDoctorToExcel(report: SchedulesDoctorExcelData) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet('Schedules by Doctor', {
        pageSetup: { orientation: 'landscape' }
    })

    ws.properties.defaultRowHeight = 20

    const headers = [
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.TIME'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.RANG_TIME'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.CN'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.PT'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.SN'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.PATIENT_NAME'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.PHONE'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.OPERATIVE_TYPE'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.OPERATIVE'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.STATUS'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.PRICE'),
        'LAB',
        'X-Ray',
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.SPACIAL'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.PRACTICE'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.REMARK'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.CREATE_BY'),
        report.t('REPORT.TABLE.SCHEDULES_DOCTOR.UPDATE_BY'),
    ]

    if (!report.snType) {
        headers.splice(4, 1)
    }
    ws.columns = headers.map(h => ({ header: h, width: 15 }))

    const totalCols = headers.length
    let rowIndex = 1


    ws.mergeCells(rowIndex, 1, rowIndex, totalCols)
    ws.getCell(rowIndex, 1).value = report.t('REPORT.REPORT_NAME.SCHEDULES_DOCTOR')
    ws.getCell(rowIndex, 1).font = { size: 16, bold: true }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'center' }
    rowIndex += 1

    ws.mergeCells(rowIndex, 1, rowIndex, totalCols)
    ws.getCell(rowIndex, 1).value =
        `สาขา: ${report.branchName}    ` +
        `วันที่เริ่มต้น: ${report.dateStart}    ` +
        `ถึง: ${report.dateEnd}    ` +
        `ทันตแพทย์: ${report.doctorsFilter || report.t('REPORT.REPORT_GROUP.ALL')}    ` +
        `สถานะ: ${report.statusFilter || report.t('REPORT.REPORT_GROUP.ALL')}     ` +
        `${report.operativeTypeFilter || report.t('REPORT.REPORT_GROUP.ALL')}    ` +
        `${report.operativeFilter || report.t('REPORT.REPORT_GROUP.ALL')}`
    ws.getCell(rowIndex, 1).font = { size: 12 }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'left' }
    rowIndex += 2

    for (const day of report.groups) {
        // วันที่
        ws.mergeCells(rowIndex, 1, rowIndex, totalCols)
        const dateCell = ws.getCell(rowIndex, 1)
        dateCell.value = `${report.t('MOMENT_AT')} ${toBuddhistYear(moment(day.date), 'DD/MM/YYYY')}`
        dateCell.font = { bold: true }
        dateCell.alignment = { horizontal: 'left' }
        rowIndex += 2

        const byDoctor = day.obj.reduce<Record<number, { name: string; items: ScheduleItem[] }>>((acc, item) => {
            if (!acc[item.doctorId]) {
                acc[item.doctorId] = { name: item.doctorFullName, items: [] }
            }
            acc[item.doctorId].items.push(item)
            return acc
        }, {})

        for (const { name, items } of Object.values(byDoctor)) {
            // ชื่อทันตแพทย์
            ws.mergeCells(rowIndex, 1, rowIndex, totalCols)
            const docCell = ws.getCell(rowIndex, 1)
            docCell.value = `${report.t('REPORT.FILTER.DOCTOR')} ${name}`
            docCell.font = { bold: true }
            docCell.alignment = { horizontal: 'left' }
            rowIndex += 2

            const hdrRow = ws.addRow(headers)
            hdrRow.eachCell(cell => {
                cell.font = { bold: true }
                cell.alignment = { horizontal: 'center' }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                }
            })
            rowIndex += 1

            for (const it of items) {
                const values = [
                    moment(it.timeStart, 'HH:mm:ss').format('HH:mm'),
                    `${it.durationMinute} นาที`,
                    it.cnNumber ?? '-',
                    it.branchCnNumber ?? '-',
                    it.snCode ?? '-',
                    it.patientFullName,
                    it.phone ?? '-',
                    it.operativeTypeName ?? '-',
                    it.operativeName,
                    it.statusAppointment
                        ? report.t(`APPOINTMENT.STATUS.${it.statusAppointment}`)
                        : '-',
                    it.price,
                    report.t(it.lab),
                    report.t(it.xray),
                    it.specialInfo ?? '-',
                    it.practice || '-',
                    it.remark || '-',
                    it.createdBy,
                    it.updatedBy
                ]

                if (!report.snType) {
                    values.splice(4, 1)
                }


                const dataRow = ws.addRow(values)
                dataRow.eachCell((cell, col) => {
                    // set border
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    }

                    if (col === 11) {
                        cell.numFmt = '#,##0.00'
                        cell.alignment = { horizontal: 'right' }
                    } else if (typeof cell.value === 'number') {
                        cell.alignment = { horizontal: 'right' }
                    } else {
                        cell.alignment = { horizontal: 'left' }
                    }
                })
                rowIndex += 1
            }

            rowIndex += 2
        }

        rowIndex += 2
    }
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${report.t(`REPORT.REPORT_NAME.SCHEDULES_DOCTOR`)} (${report.dateStart}-${report.dateEnd}).xlsx`
    )
}
